import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody
} from '@ps-design-system/components/Dialog';
import PhotoIcon from '@ps-design-system/icons/Photo/Photo20';
import PlayCircleIcon from '@ps-design-system/icons/PlayCircle/PlayCircle20';
import CubeIcon from '@ps-design-system/icons/Cube/Cube20';
import ArrowRightIcon from '@ps-design-system/icons/ArrowRight/ArrowRight20';
import LinkIcon from '@ps-design-system/icons/Link/Link20';

import UploadMediaCardWeb from './web/UploadMediaCardWeb';
import UploadMediaCardMobile from './mobile/UploadMediaCardMobile';

import { useUploadModal } from '../context/UploadModalContext';
import { useUploaderPageData } from '../context/UploaderPageContext';
import { useUploaderActions } from '../hooks/useUploaderActions';

const UploaderMediaOptions = ({ onNext }) => {
  const { handlePhotoUpload } = useUploaderActions();
  const { intl, isMobile } = useUploaderPageData();
  const MediaCard = isMobile ? UploadMediaCardMobile : UploadMediaCardWeb;
  const { closeMediaModal } = useUploadModal();
  const buttonTranslations = {
    photo: {
      id: isMobile ? 'uploader.media_modal.add' : 'uploader.media_modal.photos_button',
      defaultMessage: isMobile ? 'Add' : 'Add photos'
    },
    video: {
      id: isMobile ? 'uploader.media_modal.link' : 'uploader.media_modal.videos_button',
      defaultMessage: isMobile ? 'Link' : 'Link videos'
    },
    '3d': {
      id: isMobile ? 'uploader.media_modal.link' : 'uploader.media_modal.three_d_tour_button',
      defaultMessage: isMobile ? 'Link' : 'Link 3D tour'
    }
  };

  const handlePhotoClick = () => {
    closeMediaModal();
    handlePhotoUpload();
  };

  return (
    <>
      <DialogHeader
        headerAlignment="left"
        showCloseButton
      >
        <DialogTitle className="uploader-dialog-header">
          <FormattedMessage id="uploader.media_modal.header" defaultMessage="Choose your media type"/>
        </DialogTitle>
        <DialogDescription>
          <FormattedMessage id="uploader.media_modal.description" defaultMessage="Add photos, videos, and a 3D tour to help guests picture your space and get excited to book."/>
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <div className="uploader-media-modal">
          <MediaCard
            startIcon={<PhotoIcon />}
            heading={<FormattedMessage id="uploader.media_modal.photos" defaultMessage="Photos"/>}
            body={<FormattedMessage id="uploader.media_modal.photos_description" defaultMessage="Use high-quality photos that show off your space and give guests a clear idea of what to expect."/>}
            helperText={<FormattedMessage id="uploader.media_modal.photos_caption" defaultMessage="Visible throughout search and listing page"/>}
            buttonLabel={intl.formatMessage({ id: buttonTranslations.photo.id, defaultMessage: buttonTranslations.photo.defaultMessage })}
            buttonIcon={<ArrowRightIcon />}
            onClick={handlePhotoClick}
            attributes={{ 'data-testing-id': 'modal-add-photo' }}
          />
          <MediaCard
            startIcon={<PlayCircleIcon />}
            heading={<FormattedMessage id="uploader.media_modal.videos" defaultMessage="Videos"/>}
            body={<FormattedMessage id="uploader.media_modal.videos_description" defaultMessage="Link YouTube and Vimeo videos to showcase your space and help guests envision their booking."/>}
            helperText={<FormattedMessage id="uploader.media_modal.videos_caption" defaultMessage="Visible within listing gallery"/>}
            buttonLabel={intl.formatMessage({ id: buttonTranslations.video.id, defaultMessage: buttonTranslations.video.defaultMessage })}
            buttonIcon={<LinkIcon />}
            onClick={() => onNext('add-videos')}
            attributes={{ 'data-testing-id': 'modal-add-video' }}
          />
          <MediaCard
            startIcon={<CubeIcon />}
            heading={<FormattedMessage id="uploader.media_modal.three_d_tour" defaultMessage="3D tour"/>}
            body={<FormattedMessage id="uploader.media_modal.three_d_tour_description" defaultMessage="Use Matterport to give guests a 3D walk through to visualize your space and understand the layout."/>}
            helperText={<FormattedMessage id="uploader.media_modal.three_d_tour_caption" defaultMessage="Visible within listing gallery"/>}
            buttonLabel={intl.formatMessage({ id: buttonTranslations['3d'].id, defaultMessage: buttonTranslations['3d'].defaultMessage })}
            buttonIcon={<LinkIcon />}
            onClick={() => onNext('add-3d-tour')}
            attributes={{ 'data-testing-id': 'modal-add-3d' }}
          />
        </div>
      </DialogBody>
    </>
  );
};

UploaderMediaOptions.propTypes = {
  onNext: PropTypes.func
};

UploaderMediaOptions.defaultProps = {
  onNext: () => {}
};

export default UploaderMediaOptions;
